import { Directive, ElementRef, HostListener, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
    selector: 'input[bpCurrencyFormat]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CurrencyFormatterDirective),
            multi: true
        }
    ]
})
export class CurrencyFormatterDirective implements ControlValueAccessor, OnInit {
    @Input() allowDecimals: boolean = false;
    @Input() maxDecimalPlaces: number = 2;

    private onChange = (_: any) => {};
    private onTouched = () => {};

    private locale: string = 'en-AU';

    constructor(private el: ElementRef<HTMLInputElement>) {}

    ngOnInit(): void {
        this.el.nativeElement.type = 'text';
        this.el.nativeElement.inputMode = this.allowDecimals ? 'decimal' : 'numeric';
    }

    writeValue(value: number | string | null): void {
        const formattedValue = this.formatValue(value, false);
        this.el.nativeElement.value = formattedValue;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.el.nativeElement.disabled = isDisabled;
    }

    @HostListener('input', ['$event'])
    onInput(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const rawValue = inputElement.value;
        const originalCursorPos = inputElement.selectionStart;

        let numericString = rawValue.replace(/[^0-9.]/g, '');
        if (this.allowDecimals) {
            const parts = numericString.split('.');
            if (parts.length > 2) {
                numericString = parts[0] + '.' + parts.slice(1).join('');
            }
            if (parts[1] && parts[1].length > this.maxDecimalPlaces) {
                numericString = parts[0] + '.' + parts[1].substring(0, this.maxDecimalPlaces);
            }
        } else {
            numericString = numericString.split('.')[0];
        }

        let numericValue: number | null = null;
        if (numericString === '') {
            numericValue = null;
        } else if (this.allowDecimals) {
            if (numericString === '.') numericValue = null;
            else numericValue = parseFloat(numericString);
        } else {
            numericValue = parseInt(numericString, 10);
        }

        this.onChange(numericValue === null || isNaN(numericValue as any) ? null : numericValue);

        const formattedDisplayValue = this.formatValue(numericString, true);
        inputElement.value = formattedDisplayValue;

        if (originalCursorPos !== null) {
            let numNumericCharsBeforeOriginalCursor = 0;
            const rawValueSubstring = rawValue.substring(0, originalCursorPos);
            for (let i = 0; i < rawValueSubstring.length; i++) {
                if (/[0-9]/.test(rawValueSubstring[i]) || (this.allowDecimals && rawValueSubstring[i] === '.')) {
                    numNumericCharsBeforeOriginalCursor++;
                }
            }

            let newCursorPos = 0;
            let numericCharsCounted = 0;
            for (
                let i = 0;
                i < formattedDisplayValue.length && numericCharsCounted < numNumericCharsBeforeOriginalCursor;
                i++
            ) {
                newCursorPos++;
                if (
                    /[0-9]/.test(formattedDisplayValue[i]) ||
                    (this.allowDecimals && formattedDisplayValue[i] === '.')
                ) {
                    numericCharsCounted++;
                }
            }
            if (this.allowDecimals && numericString.endsWith('.') && formattedDisplayValue.endsWith('.')) {
                newCursorPos = formattedDisplayValue.length;
            }

            try {
                inputElement.setSelectionRange(newCursorPos, newCursorPos);
            } catch (e) {
                console.warn('CurrencyFormatterDirective: Could not set selection range.', e);
            }
        }
    }

    @HostListener('blur')
    onBlur(): void {
        this.onTouched();
        const rawValueFromInput = this.el.nativeElement.value;
        let currentNumericString = rawValueFromInput.replace(/[^0-9.]/g, '');
        if (this.allowDecimals) {
            const parts = currentNumericString.split('.');
            if (parts.length > 2) {
                currentNumericString = parts[0] + '.' + parts.slice(1).join('');
            }
        } else {
            currentNumericString = currentNumericString.split('.')[0];
        }

        const finalFormattedValue = this.formatValue(currentNumericString, false);
        this.el.nativeElement.value = finalFormattedValue;

        let numericValue: number | null = null;
        if (currentNumericString === '' || (this.allowDecimals && currentNumericString === '.')) {
            numericValue = null;
        } else if (this.allowDecimals) {
            const valueToParse = currentNumericString.endsWith('.')
                ? currentNumericString.slice(0, -1)
                : currentNumericString;
            if (valueToParse === '') numericValue = null;
            else numericValue = parseFloat(valueToParse);
        } else {
            if (currentNumericString === '') numericValue = null;
            else numericValue = parseInt(currentNumericString, 10);
        }
        this.onChange(numericValue === null || isNaN(numericValue as any) ? null : numericValue);
    }

    private formatIntegerWithCommas(integerPart: string): string {
        if (!integerPart || integerPart === 'NaN') {
            return '';
        }
        try {
            const num = Number(integerPart);
            if (isNaN(num)) return integerPart;

            return new Intl.NumberFormat(this.locale, {
                useGrouping: true,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(num);
        } catch (e) {
            console.warn('CurrencyFormatterDirective: Error formatting integer part', e);
            return integerPart;
        }
    }

    private formatValue(value: number | string | null, isInputEvent: boolean): string {
        if (value === null || value === undefined) {
            return '';
        }
        let numStr = String(value).trim();
        if (numStr === '') {
            return '';
        }

        numStr = numStr.replace(/,/g, '');

        if (this.allowDecimals && isInputEvent && numStr.endsWith('.')) {
            const integerPart = numStr.slice(0, -1);
            if (integerPart === '') return '.';
            return this.formatIntegerWithCommas(integerPart) + '.';
        }

        const [integerPartString, decimalPartStringInput = ''] = numStr.split('.');

        const formattedInteger =
            integerPartString === '' && decimalPartStringInput !== ''
                ? '0'
                : this.formatIntegerWithCommas(integerPartString);

        if (this.allowDecimals) {
            let decimals = decimalPartStringInput.substring(0, this.maxDecimalPlaces);

            if (numStr.includes('.')) {
                if (!isInputEvent && decimals === '' && numStr.includes('.')) {
                    return formattedInteger;
                }
                return `${formattedInteger}.${decimals}`;
            }
            return formattedInteger;
        } else {
            return formattedInteger;
        }
    }
}
